import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import XDraftActions from "../../components/x/XDraftActions";
import { formatAthens } from "./SocialOverview";

const GROUPS = [
  ["draft", "Drafts", "Never posted. Edit, schedule or delete them here."],
  ["pending", "Queued", "Posted automatically at the scheduled time through the official X API."],
  ["failed", "Failed", "The X API refused these; check the error, then re-create them in the Composer."],
];

// Social > X queue: the same rows as /api/x/queue. Drafts use the existing
// PATCH /api/x/queue/{id}, POST /api/x/queue/{id}/schedule and
// DELETE /api/x/queue/{id}/draft endpoints (via XDraftActions); queued posts can
// be cancelled (DELETE /api/x/queue/{id}). Nothing here posts immediately.
export default function SocialQueue() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/api/x/queue");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load the X queue.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async (row) => {
    if (!window.confirm("Cancel this queued post? It will not be posted.")) return;
    try {
      await api.delete(`/api/x/queue/${row.id}`);
      toast.success("Queued post cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel");
    }
  };

  if (error) return <div role="alert" className="rounded-xl border border-red-900 bg-red-950/30 p-5 text-sm text-red-200">{error}</div>;
  if (!rows) return <p className="text-sm text-zinc-400">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="social-queue">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-400">{rows.length ? `${rows.length} items in the X queue.` : "The X queue is empty."}</p>
        <Link to="/crm/x" className="rounded bg-[#FF10F0] px-3 py-1.5 text-xs font-semibold text-black hover:bg-pink-400">New post in Composer</Link>
      </div>
      {GROUPS.map(([status, label, help]) => {
        const items = rows.filter((r) => (r.status || "pending") === status);
        return (
          <section key={status} data-testid={`social-queue-${status}`} className="rounded-xl border border-[#FF10F0]/25 bg-black/60 p-4">
            <h3 className="text-sm font-semibold text-white">{label} <span className="text-zinc-500">({items.length})</span></h3>
            <p className="mb-3 text-xs text-zinc-500">{help}</p>
            {items.length === 0 ? <p className="text-xs text-zinc-500">None.</p> : (
              <ul className="space-y-3">
                {items.map((r) => (
                  <li key={r.id} className="rounded-lg border border-zinc-800 p-3">
                    <p className="whitespace-pre-wrap text-sm text-zinc-100">{r.text}</p>
                    <p className="mt-1 text-xs text-zinc-500">{formatAthens(r.scheduled_at)}{r.error ? ` · ${r.error}` : ""}</p>
                    <div className="mt-2">
                      {status === "draft" && <XDraftActions draft={r} onChanged={load} />}
                      {status === "pending" && (
                        <button type="button" onClick={() => cancel(r)} className="text-[11px] font-bold uppercase tracking-wider text-red-300 hover:underline">Cancel</button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
